import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { bindTypes, IRefReportModal, IReport, IWdtba003, IWdtba009 } from "../interface";
import { addAndChangeSchema } from "../schemas";
import TabBinds from "./Tabs/tabBinds";
import TabCollumns from "./Tabs/tabCollumns";
import TabGeneralData from "./Tabs/tabGeneralData";

const ReportCrudModal: React.ForwardRefRenderFunction<IRefReportModal> = (_, ref) => {
	const { translate} = useTranslation();
	const { addToast} = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const controllerUrl = "reports/";
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const [modalTitle,setModalTitle] = useState<string>("");
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const [isDuplicate, setIsDuplicate] = useState(false);
	const [report, setReport] = useState<IReport>({} as IReport);
	const [binds, setBinds] = useState<IWdtba009[]>([] as IWdtba009[]);
	const [columns, setColumns] = useState<IWdtba003[]>([] as IWdtba003[]);
	const [onCloseFunction,setOnCloseFunction] = useState<((report:IReport)=>void )>();

	const modalReport = useRef<RCCRefModalProps>(null);

	const { 
		register,
		control, 
		unregister,
		formState, 
		setError,
		clearErrors,
		setValue,
		reset,
		handleSubmit,
		getValues
	}= useForm<IReport>();

	const submitReport = async(data: any) => {
		try{
			clearErrors();
			modalReport.current?.setLoadingButton();
			
			const dataForm = data as IReport;

			// sicroniza estados com os valores do form
			dataForm.binds = binds?.map((x) => {
				const bind = dataForm.binds?.find((y) => y.id === x.id); 
				if(bind){
					return {...x,...bind};
				}
				return x;
				
			});

			dataForm.columns = columns?.map((x) => {
				const col = dataForm.columns?.find((y) => y.id === x.id); 
				if(col){
					return {...x,...col};
				}
				return x;
				
			});

			setBinds(dataForm.binds);
			setColumns(dataForm.columns);

			if(dataForm.binds.length > 0){
				dataForm.binds = dataForm.binds.map((bind,bindIndex) => {
					const newBind = {...bind};
					newBind.wda009Ordem = bindIndex+1;
					if(newBind.wda009Tipo === bindTypes.OPTIONS_LIST){
						newBind.options = newBind.options?.map((opt,index) => ({...opt,wda010Opcao:index+1}));
					}
					return newBind;
				});
			}

			await addAndChangeSchema(translate).validate({...dataForm}, {
				abortEarly: false
			});
			
			let response;
			if(isAdd || isDuplicate){
				response = await RCCServicesApi.postRequest({
					method: `${controllerUrl}Insert`,
					body: {
						...dataForm	
					}
				});

			}else{
				response = await RCCServicesApi.putRequest({
					method: `${controllerUrl}Update`,
					body: {
						...dataForm
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd||isDuplicate ? translate("Relatório adicionado com sucesso") : translate("Relatório alterado com sucesso"),
				title: translate("Sucesso")
			});

			if(onCloseFunction)
				onCloseFunction(response.data as IReport);

			modalReport.current?.resetLoadingButton();
			modalReport.current?.closeModal();

		}catch(err){
			modalReport.current?.resetLoadingButton();

			if(getValidationErrors(err, 
				setError)){
				
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, isAdd||isDuplicate ? translate("Erro ao adicionar relatório") : translate("Erro ao alterar relatório"), addToast);
		}
	};

	const actionButtonAdd = (onClose?:(report?:IReport)=>void) => {
		reset();
		setReport({} as IReport);
		setBinds([] as IWdtba009[]);
		setColumns([] as IWdtba003[]);
		setSubtitle(undefined);
		setIsAdd(true); 
		setOnCloseFunction(() => onClose);
		setModalTitle(translate("Adicionar relatório"));
		modalReport.current?.openModal();
	};

	const actionButtonChange = async (id:number,duplicate:boolean,onClose?:(report:IReport)=>void) => {
		try {
			setLoading(true);
			reset();
			setIsAdd(false);
			setIsDuplicate(duplicate);
			setOnCloseFunction(() => onClose);

			if(duplicate){
				setModalTitle(translate("Copiar relatório"));
			}else{
				setModalTitle(translate("Alterar relatório"));
			}

			await requestReport(id,duplicate);
			setLoading(false);
			modalReport.current?.openModal();
		} catch (error) {
			standardErrorMessages(error,translate("Erro ao encontrar relatório"), addToast);
			setLoading(false);
		}
	};

	const requestReport = async(id:number,duplicate:boolean) => {
		
		const {data} = await RCCServicesApi.getRequest({
			method: `${controllerUrl}Select`,
			urlParameters: {
				id
			}
		});

		setReport(() => {
			if(duplicate){
				const newTitle = data.wda002Titulo.length > 191 ? `(${translate("CÓPIA")}) ${data.wda002Titulo.substr(0, 192)}`: `(${translate("CÓPIA")}) ${data.wda002Titulo}`;
				return {...data,wda002Titulo:newTitle};
			}
			return data;
		});
		setBinds(data.binds.map((x:IWdtba009) => x));
		setColumns(data.columns.map((x:IWdtba003) => x));

		setValue("binds",data.binds.map((x:IWdtba009) => x));
		setValue("columns",data.columns.map((x:IWdtba003) => x));
		
		setSubtitle(data.wda002Titulo);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange
	}));

	return (
		<Modal
			ref={modalReport}
			header={{
				title:modalTitle,
				icon: {
					name: "chartBar"
				},
				subtitle: subtitle ? `${translate("Relatório")}: ${subtitle}` : undefined
			}}
			onSubmit={handleSubmit(submitReport)}
		>	
			<Tabs
				name="reportsTab"
				tabInit={0}
				options={
					[
						{
							label: translate("Dados"),
							content: 
                            <TabGeneralData 
                            	report={report}
                            	binds={binds}
                            	setBinds={setBinds}
                            	columns={columns}
                            	setColumns={setColumns}
                            	errors={formState.errors}
                            	register={register}
                            	control={control}
                            	unregister={unregister}
                            	setValue={setValue}
                            />
						},
						{
							label: translate("Binds"),
							content: 
                            <TabBinds
                            	binds={binds}
                            	setBinds={setBinds}
                            	errors={formState.errors}
                            	register={register}
                            	control={control}
                            	setValue={setValue}
                            	unregister={unregister}
                            	getValues={getValues}
                            />
						},
						{
							label: translate("Campos"),
							content: 
							<TabCollumns
								columns={columns}
								setColumns={setColumns}
								errors={formState.errors}
								register={register}
								control={control}
								setValue={setValue}
								unregister={unregister}
								getValues={getValues}
							/>
						}
					]
				}
			/>
		</Modal>
	);
};

export default forwardRef(ReportCrudModal);
